import React, { useEffect, useState } from 'react';
import { useRealTimeData } from '../hooks/useRealTimeData';
import ConnectionStatus from '../components/ConnectionStatus';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Button,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  Divider,
  Badge,
  IconButton,
  CircularProgress,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import EmergencyChronometer from '../components/EmergencyChronometer';
import {
  Warning as EmergencyIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Navigation as NavigationIcon,
  Error as WarningIcon,
  CheckCircle as CheckIcon,
  Notifications as NotificationIcon,
  LocationOn as LocationIcon,
  Psychology as AIIcon,
  Info as InfoIcon,
  Timer as TimerIcon,
  AirportShuttle as AmbulanceIcon,
  Room as RoomIcon,
  MedicalServices as MedicalIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

interface SymptomReport {
  id: string;
  patientName: string;
  age: number;
  symptoms: string[];
  severity: 'emergency' | 'urgent' | 'moderate' | 'mild';
  confidence: number;
  timestamp: string;
  status: 'pending_review' | 'reviewed';
}

interface IntegratedEmergencyCase {
  id: string;
  patientId: string;
  patientName: string;
  age: number;
  severity: 'stroke_suspected' | 'cardiac_event' | 'general_emergency';
  triggerTime: string;
  aiAssessment: {
    confidence: number;
    urgencyScore: number;
    recommendation: string;
  };
  ambulance: {
    id: string;
    status: 'dispatched' | 'en_route_to_patient' | 'with_patient' | 'en_route_to_hospital' | 'arrived_hospital';
    location: string;
    eta: string;
    distance: string;
    crew: { paramedic: string; driver: string };
  };
  hospital: {
    preparation: {
      roomAssigned: boolean;
      doctorNotified: boolean;
      equipmentReady: boolean;
      medicationsPrepared: boolean;
      labAlerted: boolean;
      imagingReady: boolean;
    };
    assignedTeam: {
      doctor: string;
      nurse: string;
      room: string;
    };
  };
  family: {
    primaryContact: string;
    phone: string;
    notified: boolean;
  };
  currentSymptoms: string[];
  medicalHistory: string[];
}

const DoctorDashboard: React.FC = () => {
  // Use real-time data for symptom reports
  const { 
    data: symptomReports, 
    loading: loadingReports, 
    error: symptomError 
  } = useRealTimeData<SymptomReport[]>('symptom-reports', [], async () => {
    // Fallback fetch function (would be real API call in production)
    const response = await fetch('http://localhost:3000/api/v1/symptoms/reports');
    const data = await response.json();
    return data.success ? data.data.recentReports : [];
  });
  const [integratedEmergencies, setIntegratedEmergencies] = useState<IntegratedEmergencyCase[]>([
    {
      id: 'EMG001',
      patientId: '1',
      patientName: 'Juan Pérez',
      age: 68,
      severity: 'stroke_suspected',
      triggerTime: new Date(Date.now() - 12 * 60 * 1000).toISOString(), // 12 minutes ago
      aiAssessment: {
        confidence: 0.92,
        urgencyScore: 85,
        recommendation: 'ACV SOSPECHOSO - Protocolo de emergencia neurológica'
      },
      ambulance: {
        id: 'AMB-07',
        status: 'en_route_to_hospital',
        location: 'En ruta - Av. Brasil',
        eta: '6 minutos',
        distance: '2.3 km',
        crew: { paramedic: 'Carlos Mendoza', driver: 'Ana Rojas' }
      },
      hospital: {
        preparation: {
          roomAssigned: true,
          doctorNotified: true,
          equipmentReady: false,
          medicationsPrepared: false,
          labAlerted: true,
          imagingReady: false
        },
        assignedTeam: {
          doctor: 'Dr. Roberto Silva (Neurólogo)',
          nurse: 'Enf. Carmen Rojas',
          room: 'Emergencia 3'
        }
      },
      family: {
        primaryContact: 'María González (Hija)',
        phone: '+56912345678',
        notified: true
      },
      currentSymptoms: ['Debilidad en brazo izquierdo', 'Dificultad para hablar', 'Confusión'],
      medicalHistory: ['Hipertensión', 'Diabetes', 'Historial de ACV']
    }
  ]);
  const [emergencies, setEmergencies] = useState([
    {
      id: 1,
      patient: 'Juan Rodríguez',
      type: 'Infarto',
      severity: 'critical',
      location: 'Chillán Centro',
      time: '2 min ago',
      status: 'active',
      distance: '1.2 km'
    },
    {
      id: 2,
      patient: 'María González',
      type: 'ACV',
      severity: 'high',
      location: 'Los Angeles',
      time: '8 min ago',
      status: 'en_ruta',
      distance: '5.8 km'
    },
    {
      id: 3,
      patient: 'Carlos López',
      type: 'Arritmia',
      severity: 'medium',
      location: 'Concepción',
      time: '15 min ago',
      status: 'atendido',
      distance: '15.2 km'
    }
  ]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'error';
      case 'en_ruta': return 'warning';
      case 'atendido': return 'success';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Activa';
      case 'en_ruta': return 'En Ruta';
      case 'atendido': return 'Atendido';
      default: return status;
    }
  };

  const getSymptomSeverityColor = (severity: string) => {
    switch (severity) {
      case 'emergency': return 'error';
      case 'urgent': return 'warning';
      case 'moderate': return 'info';
      case 'mild': return 'success';
      default: return 'default';
    }
  };

  const getSymptomSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'emergency': return <WarningIcon />;
      case 'urgent': return <TimerIcon />;
      case 'moderate': return <InfoIcon />;
      case 'mild': return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min ago`;
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return `${diffHours}h ago`;
    }
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Panel Médico
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ConnectionStatus variant="chip" showLastUpdate />
          <Badge badgeContent={2} color="error">
            <NotificationIcon />
          </Badge>
        </Box>
      </Box>
      
      {/* Status Cards */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <EmergencyIcon color="error" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="error">2</Typography>
              <Typography variant="body2">Emergencias Activas</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssignmentIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="warning.main">5</Typography>
              <Typography variant="body2">Citas Hoy</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PersonIcon color="success" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="success.main">12</Typography>
              <Typography variant="body2">Pacientes Asignados</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimeIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h4" color="primary">4.2</Typography>
              <Typography variant="body2">Tiempo Respuesta (min)</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* AI Symptom Reports */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AIIcon sx={{ mr: 1 }} color="primary" />
              Reportes de Síntomas IA
              {loadingReports && <CircularProgress size={20} sx={{ ml: 2 }} />}
            </Typography>
            
            {loadingReports ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Cargando reportes...</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {(symptomReports || []).map((report) => (
                  <Grid item xs={12} md={6} lg={4} key={report.id}>
                    <Card sx={{ 
                      border: report.severity === 'emergency' ? '2px solid #f44336' : 
                              report.severity === 'urgent' ? '2px solid #ff9800' : 'none',
                      position: 'relative'
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {report.patientName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {report.age} años • {formatTimestamp(report.timestamp)}
                            </Typography>
                          </Box>
                          {report.status === 'pending_review' && (
                            <Badge 
                              badgeContent="NUEVO" 
                              color="error" 
                              sx={{ 
                                '& .MuiBadge-badge': { 
                                  fontSize: '0.6rem',
                                  height: '16px',
                                  minWidth: '16px'
                                }
                              }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Chip 
                            icon={getSymptomSeverityIcon(report.severity)}
                            label={report.severity.toUpperCase()} 
                            color={getSymptomSeverityColor(report.severity) as any}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip 
                            label={`${(report.confidence * 100).toFixed(0)}% confianza`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          Síntomas reportados:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {report.symptoms.slice(0, 3).map((symptom, index) => (
                            <Chip 
                              key={index}
                              label={symptom} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {report.symptoms.length > 3 && (
                            <Chip 
                              label={`+${report.symptoms.length - 3} más`} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="contained" 
                            color="primary"
                            onClick={() => {/* Navigate to patient details */}}
                          >
                            Ver Detalles
                          </Button>
                          {report.severity === 'emergency' && (
                            <Button 
                              size="small" 
                              variant="outlined" 
                              color="error"
                              startIcon={<PhoneIcon />}
                              onClick={() => {/* Call patient */}}
                            >
                              Llamar
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {(!symptomReports || symptomReports.length === 0) && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No hay reportes de síntomas recientes
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Integrated Emergency Cases */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <MedicalIcon sx={{ mr: 1 }} color="error" />
              Vista Integrada de Emergencias
            </Typography>
            
            {integratedEmergencies.map((emergency) => (
              <Card key={emergency.id} sx={{ mb: 3, border: '2px solid #f44336' }}>
                <CardContent>
                  {/* Patient Header with Emergency Chronometer */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'error.main', width: 56, height: 56 }}>
                        {emergency.patientName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {emergency.patientName} ({emergency.age} años)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {emergency.id} • {emergency.severity.replace('_', ' ').toUpperCase()}
                        </Typography>
                        <Chip 
                          label={`IA: ${(emergency.aiAssessment.confidence * 100).toFixed(0)}% confianza`}
                          color="primary"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <EmergencyChronometer 
                      startTime={emergency.triggerTime}
                      variant="compact"
                      size="large"
                    />
                  </Box>

                  {/* AI Assessment Alert */}
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {emergency.aiAssessment.recommendation}
                    </Typography>
                    <Typography variant="body2">
                      Puntuación de urgencia: {emergency.aiAssessment.urgencyScore}/100
                    </Typography>
                  </Alert>

                  <Grid container spacing={2}>
                    {/* Ambulance Status */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <AmbulanceIcon sx={{ mr: 1, color: 'warning.main' }} />
                            Ambulancia {emergency.ambulance.id}
                          </Typography>
                          <Chip 
                            label={emergency.ambulance.status.replace(/_/g, ' ').toUpperCase()}
                            color="warning"
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            📍 {emergency.ambulance.location}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            ⏱️ ETA: {emergency.ambulance.eta}
                          </Typography>
                          <Typography variant="body2">
                            📏 Distancia: {emergency.ambulance.distance}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Paramédico: {emergency.ambulance.crew.paramedic}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Hospital Preparation */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <HospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                            Preparación Hospital
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            👨‍⚕️ {emergency.hospital.assignedTeam.doctor}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            🏥 {emergency.hospital.assignedTeam.room}
                          </Typography>
                          
                          <Box sx={{ mt: 1 }}>
                            {Object.entries(emergency.hospital.preparation).map(([key, ready]) => (
                              <FormControlLabel
                                key={key}
                                control={<Checkbox checked={ready} size="small" />}
                                label={
                                  key === 'roomAssigned' ? 'Sala asignada' :
                                  key === 'doctorNotified' ? 'Doctor notificado' :
                                  key === 'equipmentReady' ? 'Equipos listos' :
                                  key === 'medicationsPrepared' ? 'Medicamentos' :
                                  key === 'labAlerted' ? 'Laboratorio alerta' :
                                  key === 'imagingReady' ? 'Imagenología' : key
                                }
                                sx={{ 
                                  display: 'block',
                                  '& .MuiFormControlLabel-label': { fontSize: '0.75rem' }
                                }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Family & Symptoms */}
                    <Grid item xs={12} md={4}>
                      <Card sx={{ bgcolor: '#e8f5e8' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <PersonIcon sx={{ mr: 1, color: 'success.main' }} />
                            Familia & Síntomas
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            📞 {emergency.family.primaryContact}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {emergency.family.phone}
                          </Typography>
                          <Chip 
                            label={emergency.family.notified ? "Familia notificada" : "Pendiente notificar"}
                            color={emergency.family.notified ? "success" : "warning"}
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Síntomas actuales:
                          </Typography>
                          {emergency.currentSymptoms.slice(0, 2).map((symptom, idx) => (
                            <Chip 
                              key={idx}
                              label={symptom}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<PhoneIcon />}
                      size="small"
                    >
                      Llamar Familia
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      size="small"
                    >
                      Ver Ubicación
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<HospitalIcon />}
                      size="small"
                    >
                      Protocolo Hospital
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* Active Emergencies */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <EmergencyIcon sx={{ mr: 1 }} color="error" />
              Otras Emergencias
            </Typography>
            
            {emergencies.map((emergency) => (
              <Card key={emergency.id} sx={{ mb: 2, border: emergency.status === 'active' ? '2px solid #f44336' : 'none' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ mr: 2, bgcolor: getSeverityColor(emergency.severity) + '.main' }}>
                          {emergency.patient.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{emergency.patient}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {emergency.type} • {emergency.time}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Chip 
                          label={emergency.severity.toUpperCase()} 
                          color={getSeverityColor(emergency.severity) as any}
                          size="small" 
                        />
                        <Chip 
                          label={getStatusText(emergency.status)} 
                          color={getStatusColor(emergency.status) as any}
                          size="small" 
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">{emergency.location}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {emergency.distance}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {emergency.status === 'active' && (
                        <>
                          <Button variant="contained" color="error" size="small" startIcon={<NavigationIcon />}>
                            Responder
                          </Button>
                          <Button variant="outlined" size="small" startIcon={<PhoneIcon />}>
                            Llamar
                          </Button>
                        </>
                      )}
                      {emergency.status === 'en_ruta' && (
                        <Button variant="contained" color="warning" size="small" startIcon={<CheckIcon />}>
                          Llegué
                        </Button>
                      )}
                      {emergency.status === 'atendido' && (
                        <Chip label="Completado" color="success" />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* Today's Schedule */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: 'fit-content' }}>
            <Typography variant="h6" gutterBottom>
              <AssignmentIcon sx={{ mr: 1 }} />
              Agenda de Hoy
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Control Sr. Martínez"
                  secondary="09:00 - Cardiología"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Consulta Sra. Fernández"
                  secondary="10:30 - Control post-operatorio"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Evaluación Sr. Torres"
                  secondary="14:00 - Primera consulta"
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemIcon>
                  <TimeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Control Sra. Vásquez"
                  secondary="15:30 - Seguimiento ACV"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              <strong>Alerta del Sistema:</strong> Incremento del 15% en emergencias cardíacas en las últimas 24 horas
            </Typography>
          </Alert>
          
          <Alert severity="info">
            <Typography variant="subtitle2">
              <strong>Recordatorio:</strong> Reunión del equipo médico a las 17:00 en Sala de Conferencias
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DoctorDashboard;